// frontend/src/pages/PreOrder.jsx - COMPLETELY CLEANED
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { useCart } from "../context/CartContext";
import { usePayment } from "../context/PaymentContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCheckCircle,
  faTractor,
  faUser,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faBox,
  faShoppingCart,
  faSpinner,
  faCalendarCheck,
  faInfoCircle,
  faMinus,
  faPlus,
  faTrash,
  faMoneyBillWave,
  faBuilding,
  faCow,
  faStore,
  faHandshake,
  faUsers,
  faClipboardCheck,
  faExclamationTriangle,
  faGlobe,
  faShip,
  faWarehouse,
  faPercent,
  faAward,
  faRocket,
  faCalculator,
  faClock,
  faChartLine,
  faWeightHanging,
  faFlask,
  faMobileAlt,
  faUniversity,
  faWallet,
  faCreditCard,
  faLock,
  faShieldAlt,
  faCheck,
  faTimes,
  faEdit,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp as faWhatsappBrand } from "@fortawesome/free-brands-svg-icons";
import { createPreOrder, getProducts } from "../api";
import SEO from "../components/SEO/SEO";

// ============================================
// CONSTANTS
// ============================================
const BASE_EXCHANGE_RATE = 24.65;
const WHATSAPP_NUMBER = "23231030808";

const GROWPRO_PRICING = {
  name: "Kenali GrowPro™",
  category: "rumen-bypass-fat",
  slug: "kenali-growpro",
  basePricePerKg: 4.2,
  packPrices: {
    25: {
      regular: 105.0,
      preOrder: 98.0,
      deposit: 49.0,
      balance: 49.0,
      totalKg: 25,
      feedMixKg: 6250,
      savings: 7.0,
      savingsPercent: 7,
    },
    50: {
      regular: 195.0,
      preOrder: 180.0,
      deposit: 90.0,
      balance: 90.0,
      totalKg: 50,
      feedMixKg: 12500,
      savings: 15.0,
      savingsPercent: 8,
    },
  },
};

const PAYMENT_METHODS_CONFIG = [
  { value: "cash", label: "Cash", icon: faMoneyBillWave },
  { value: "orange_money", label: "Orange Money", icon: faMobileAlt },
  { value: "afrimoney", label: "AfriMoney", icon: faMobileAlt },
  { value: "wave", label: "WAVE", icon: faMobileAlt },
  { value: "bank_transfer", label: "Bank Transfer", icon: faUniversity },
  { value: "mobile_money", label: "Mobile Money", icon: faMobileAlt },
];

const ANIMAL_TYPES = [
  { value: "cattle", label: "Cattle" },
  { value: "goats", label: "Goats" },
  { value: "sheep", label: "Sheep" },
  { value: "pigs", label: "Pigs" },
  { value: "poultry", label: "Poultry" },
  { value: "other", label: "Other" },
];

const CUSTOMER_TYPES = [
  { value: "livestock_farmer", label: "Livestock Farmer", icon: faTractor },
  { value: "cooperative", label: "Cooperative", icon: faUsers },
  { value: "feed_shop", label: "Feed Shop", icon: faStore },
  { value: "reseller", label: "Reseller", icon: faHandshake },
  { value: "agro_dealer", label: "Agro Dealer", icon: faBuilding },
  { value: "other", label: "Other", icon: faInfoCircle },
];

const PICKUP_TYPES = [
  { value: "pickup", label: "Pickup from Kenali Enterprise" },
  { value: "delivery", label: "Delivery to my location" },
  { value: "to_be_confirmed", label: "To be confirmed" },
];

// ============================================
// HELPERS
// ============================================
const normalizePhone = (value = "") => value.replace(/[^0-9+]/g, "").trim();

const formatSLL = (value) => {
  if (!value && value !== 0) return "Le 0";
  return new Intl.NumberFormat("en-SL", {
    style: "currency",
    currency: "SLL",
    maximumFractionDigits: 0,
  }).format(Math.round(value || 0));
};

const formatUSD = (value) => {
  if (!value && value !== 0) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);
};

// ============================================
// FEED CALCULATOR COMPONENT
// ============================================
const FeedCalculator = ({
  selectedSize,
  animalCount,
  setAnimalCount,
  feedPerDay,
  setFeedPerDay,
}) => {
  const pricing = GROWPRO_PRICING.packPrices[selectedSize];
  const totalGrowProKg = pricing?.totalKg || 25;
  const totalFeedMixKg = pricing?.feedMixKg || 6250;

  const dailyGrowProNeeded = animalCount * 1;
  const dailyFeedNeeded = animalCount * (feedPerDay || 250);
  const daysSupply =
    animalCount > 0 && dailyGrowProNeeded > 0
      ? Math.floor(totalGrowProKg / dailyGrowProNeeded)
      : 0;

  return (
    <div className="feed-calculator">
      <div className="calculator-header">
        <FontAwesomeIcon icon={faCalculator} />
        <h4>Feed Mixing Calculator</h4>
        <span className="calculator-badge">1kg GrowPro : 250kg Feed</span>
      </div>

      <div className="calculator-grid">
        <div className="calculator-inputs">
          <div className="form-group">
            <label htmlFor="animalCount">
              <FontAwesomeIcon icon={faCow} /> Number of Animals
            </label>
            <input
              type="number"
              id="animalCount"
              value={animalCount}
              onChange={(e) =>
                setAnimalCount(Math.max(0, parseInt(e.target.value) || 0))
              }
              min="0"
              placeholder="Enter number of animals"
              className="calculator-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="feedPerDay">
              <FontAwesomeIcon icon={faWeightHanging} /> Feed Per Animal/Day
              (kg)
            </label>
            <input
              type="number"
              id="feedPerDay"
              value={feedPerDay}
              onChange={(e) =>
                setFeedPerDay(Math.max(0, parseInt(e.target.value) || 0))
              }
              min="0"
              placeholder="250 kg (default)"
              className="calculator-input"
            />
            <small className="input-hint">
              Recommended: 250kg per cattle/day
            </small>
          </div>
        </div>

        <div className="calculator-results">
          <div className="result-item">
            <span className="result-label">Selected Pack</span>
            <span className="result-value">{selectedSize}kg GrowPro</span>
          </div>
          <div className="result-item highlight">
            <span className="result-label">Total Feed Mix</span>
            <span className="result-value">
              {totalFeedMixKg.toLocaleString()} kg
            </span>
          </div>
          {animalCount > 0 && (
            <>
              <div className="result-item">
                <span className="result-label">Daily GrowPro Needed</span>
                <span className="result-value">{dailyGrowProNeeded} kg</span>
              </div>
              <div className="result-item">
                <span className="result-label">Daily Feed Mix Needed</span>
                <span className="result-value">
                  {dailyFeedNeeded.toLocaleString()} kg
                </span>
              </div>
              <div className="result-item highlight">
                <span className="result-label">Days Supply</span>
                <span className="result-value">{daysSupply} days</span>
              </div>
            </>
          )}
        </div>
      </div>

      {animalCount > 0 && (
        <div className="calculator-timeline">
          <div className="timeline-header">
            <FontAwesomeIcon icon={faClock} />
            <span>Expected Results Timeline</span>
          </div>
          <div className="timeline-steps">
            <div className="step">
              <div className="step-icon">2</div>
              <div className="step-info">
                <strong>2 Weeks</strong>
                <span>Improved feed intake & energy</span>
              </div>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-icon">4</div>
              <div className="step-info">
                <strong>4 Weeks</strong>
                <span>Better body condition & coat shine</span>
              </div>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-icon">8</div>
              <div className="step-info">
                <strong>8 Weeks</strong>
                <span>Optimal milk production & weight gain</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="calculator-note">
        <FontAwesomeIcon icon={faInfoCircle} />
        <span>
          <strong>Recommended Mixing:</strong> Mix {selectedSize}kg of Kenali
          GrowPro™ with {totalFeedMixKg.toLocaleString()}kg of feed. For best
          results, gradually introduce over 7-10 days.
        </span>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const PreOrder = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currency, getExchangeRate, formatAmount } = useCurrency();
  const { getCartCount } = useCart();

  const {
    paymentMethods,
    loading: paymentLoading,
    fetchPaymentMethods,
    addPayment,
    removePayment,
    setDefault,
    getDefaultPaymentMethod,
    getPaymentMethodById,
    hasSavedPaymentMethods,
    processPayment,
  } = usePayment();

  // State
  const [submitting, setSubmitting] = useState(false);
  const [selectedSize, setSelectedSize] = useState(25);
  const [cartItems, setCartItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [exchangeRate, setExchangeRate] = useState(BASE_EXCHANGE_RATE);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [preOrderNumber, setPreOrderNumber] = useState("");
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [selectedSavedPayment, setSelectedSavedPayment] = useState(null);
  const [animalCount, setAnimalCount] = useState(0);
  const [feedPerDay, setFeedPerDay] = useState(250);

  const [newPaymentMethod, setNewPaymentMethod] = useState({
    type: "card",
    cardNumber: "",
    expiryDate: "",
    cvv: "",
    nameOnCard: "",
    phoneNumber: "",
    isDefault: false,
  });

  const [formData, setFormData] = useState({
    customerInfo: {
      fullName: user?.name || "",
      farmOrBusinessName: "",
      phone: user?.phone || "",
      whatsapp: user?.phone || "",
      email: user?.email || "",
      location: "",
      district: "",
    },
    farmInfo: {
      customerType: "livestock_farmer",
      animalTypes: [],
      animalCount: 0,
      feedMixedAtOneTimeKg: 0,
      notes: "",
    },
    pickup: {
      type: "pickup",
      preferredDate: "",
      address: "30 Kissy Road, Freetown, Sierra Leone",
      instructions: "",
    },
    paymentMethod: "cash",
    paymentReference: "",
    mobileMoneyNumber: "",
    savedPaymentMethodId: "",
    termsAccepted: false,
    marketingConsent: false,
    customerNotes: "",
  });

  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const response = await getProducts({ limit: 10 });
        const data = response.data;
        if (data.products && data.products.length > 0) {
          setProducts(data.products);
          const growPro = data.products.find(
            (p) =>
              p.category === "rumen-bypass-fat" ||
              p.slug === "kenali-growpro" ||
              p.name?.toLowerCase().includes("growpro"),
          );
          setSelectedProduct(growPro || data.products[0]);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    if (user) {
      fetchPaymentMethods();
    }
  }, [user, fetchPaymentMethods]);

  useEffect(() => {
    if (paymentMethods.length > 0 && !formData.savedPaymentMethodId) {
      const defaultMethod = getDefaultPaymentMethod();
      if (defaultMethod) {
        setSelectedSavedPayment(defaultMethod);
        setFormData((prev) => ({
          ...prev,
          savedPaymentMethodId: defaultMethod._id,
        }));
      }
    }
  }, [paymentMethods, getDefaultPaymentMethod]);

  useEffect(() => {
    const rate = getExchangeRate("SLL");
    if (rate && rate > 0) {
      setExchangeRate(rate);
    }
  }, [getExchangeRate]);

  // ============================================
  // COMPUTED VALUES
  // ============================================
  const getPricingForSize = useCallback((size) => {
    return (
      GROWPRO_PRICING.packPrices[size] || {
        regular: 0,
        preOrder: 0,
        deposit: 0,
        balance: 0,
        totalKg: 0,
        feedMixKg: 0,
        savings: 0,
        savingsPercent: 0,
      }
    );
  }, []);

  const currentPricing = useMemo(
    () => getPricingForSize(selectedSize),
    [selectedSize, getPricingForSize],
  );
  const getRate = useCallback(
    () => exchangeRate || BASE_EXCHANGE_RATE,
    [exchangeRate],
  );

  const totals = useMemo(() => {
    if (cartItems.length === 0) {
      return {
        subtotalUSD: 0,
        depositUSD: 0,
        balanceUSD: 0,
        subtotalSLL: 0,
        depositSLL: 0,
        balanceSLL: 0,
        totalKg: 0,
        totalPacks: 0,
        savingsUSD: 0,
        savingsSLL: 0,
        regularTotalUSD: 0,
        savingsPercent: 0,
        depositPercent: 50,
      };
    }

    let subtotalUSD = 0,
      regularTotalUSD = 0,
      totalKg = 0,
      totalPacks = 0;
    cartItems.forEach((item) => {
      subtotalUSD += item.preOrderPrice * item.quantity;
      regularTotalUSD += item.regularPrice * item.quantity;
      totalKg += item.size * item.quantity;
      totalPacks += item.quantity;
    });

    const depositUSD = subtotalUSD * 0.5;
    const balanceUSD = subtotalUSD - depositUSD;
    const savingsUSD = regularTotalUSD - subtotalUSD;
    const rate = getRate();
    const savingsPercent =
      regularTotalUSD > 0 ? (savingsUSD / regularTotalUSD) * 100 : 0;

    return {
      subtotalUSD,
      depositUSD,
      balanceUSD,
      savingsUSD,
      subtotalSLL: subtotalUSD * rate,
      depositSLL: depositUSD * rate,
      balanceSLL: balanceUSD * rate,
      savingsSLL: savingsUSD * rate,
      totalKg,
      totalPacks,
      regularTotalUSD,
      savingsPercent,
      depositPercent: 50,
    };
  }, [cartItems, getRate]);

  // ============================================
  // HANDLERS
  // ============================================
  const addToCart = useCallback(() => {
    const pricing = getPricingForSize(selectedSize);
    const packSize = selectedSize;
    const existingItemIndex = cartItems.findIndex(
      (item) => item.size === packSize,
    );

    if (existingItemIndex >= 0) {
      const updatedItems = [...cartItems];
      updatedItems[existingItemIndex] = {
        ...updatedItems[existingItemIndex],
        quantity: updatedItems[existingItemIndex].quantity + 1,
      };
      setCartItems(updatedItems);
    } else {
      const newItem = {
        id: `growpro-${packSize}-${Date.now()}`,
        productId: selectedProduct?._id || "growpro",
        productName: GROWPRO_PRICING.name,
        productSlug: GROWPRO_PRICING.slug,
        category: GROWPRO_PRICING.category,
        size: packSize,
        quantity: 1,
        regularPrice: pricing.regular,
        preOrderPrice: pricing.preOrder,
        deposit: pricing.deposit,
        balance: pricing.balance,
        feedMixKg: pricing.feedMixKg,
        savings: pricing.savings,
        savingsPercent: pricing.savingsPercent,
      };
      setCartItems([...cartItems, newItem]);
    }

    if (errors.cart) setErrors((prev) => ({ ...prev, cart: null }));
  }, [selectedSize, cartItems, getPricingForSize, errors, selectedProduct]);

  const removeFromCart = useCallback(
    (id) => {
      setCartItems(cartItems.filter((item) => item.id !== id));
    },
    [cartItems],
  );

  const updateQuantity = useCallback(
    (id, newQuantity) => {
      if (newQuantity < 1) {
        removeFromCart(id);
        return;
      }
      setCartItems(
        cartItems.map((item) =>
          item.id === id ? { ...item, quantity: newQuantity } : item,
        ),
      );
    },
    [cartItems, removeFromCart],
  );

  const clearCart = useCallback(() => setCartItems([]), []);

  const handleInputChange = useCallback(
    (section, field, value) => {
      setFormData((prev) => ({
        ...prev,
        [section]: { ...prev[section], [field]: value },
      }));
      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
    },
    [errors],
  );

  const handlePaymentMethodChange = (method) => {
    setFormData((prev) => ({
      ...prev,
      paymentMethod: method,
      savedPaymentMethodId: "",
    }));
  };

  const handleSavedPaymentSelect = (methodId) => {
    const method = getPaymentMethodById(methodId);
    setSelectedSavedPayment(method);
    setFormData((prev) => ({
      ...prev,
      savedPaymentMethodId: methodId,
      paymentMethod: "saved_payment",
    }));
  };

  const handleNewPaymentChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewPaymentMethod({
      ...newPaymentMethod,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleAddPaymentMethod = async (e) => {
    e.preventDefault();
    setPaymentProcessing(true);

    try {
      const paymentData = {
        type: newPaymentMethod.type,
        isDefault: newPaymentMethod.isDefault,
      };
      if (newPaymentMethod.type === "card") {
        if (
          !newPaymentMethod.cardNumber ||
          !newPaymentMethod.expiryDate ||
          !newPaymentMethod.cvv ||
          !newPaymentMethod.nameOnCard
        ) {
          setErrors({ payment: "Please fill in all card details" });
          setPaymentProcessing(false);
          return;
        }
        paymentData.cardNumber = newPaymentMethod.cardNumber.replace(/\s/g, "");
        paymentData.expiryDate = newPaymentMethod.expiryDate;
        paymentData.cvv = newPaymentMethod.cvv;
        paymentData.nameOnCard = newPaymentMethod.nameOnCard;
      } else {
        if (!newPaymentMethod.phoneNumber) {
          setErrors({ payment: "Phone number is required for mobile money" });
          setPaymentProcessing(false);
          return;
        }
        paymentData.phoneNumber = newPaymentMethod.phoneNumber;
      }

      const response = await addPayment(paymentData);
      if (response?.success) {
        setShowAddPayment(false);
        setNewPaymentMethod({
          type: "card",
          cardNumber: "",
          expiryDate: "",
          cvv: "",
          nameOnCard: "",
          phoneNumber: "",
          isDefault: false,
        });
        const newMethod = response.data;
        setSelectedSavedPayment(newMethod);
        setFormData((prev) => ({
          ...prev,
          paymentMethod: "saved_payment",
          savedPaymentMethodId: newMethod._id,
        }));
        setErrors({});
        await fetchPaymentMethods();
      }
    } catch (error) {
      setErrors({
        payment:
          error.response?.data?.message || "Failed to add payment method",
      });
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleRemoveSavedPayment = async (methodId) => {
    if (
      window.confirm("Are you sure you want to remove this payment method?")
    ) {
      try {
        await removePayment(methodId);
        if (selectedSavedPayment?._id === methodId) {
          setSelectedSavedPayment(null);
          setFormData((prev) => ({
            ...prev,
            savedPaymentMethodId: "",
            paymentMethod: "cash",
          }));
        }
        await fetchPaymentMethods();
      } catch (error) {
        setErrors({ payment: "Failed to remove payment method" });
      }
    }
  };

  const validateForm = useCallback(() => {
    const newErrors = {};
    if (!formData.customerInfo.fullName.trim())
      newErrors.fullName = "Full name is required";
    if (!formData.customerInfo.phone.trim())
      newErrors.phone = "Phone number is required";
    if (!formData.customerInfo.location.trim())
      newErrors.location = "Location is required";
    if (cartItems.length === 0)
      newErrors.cart = "Please add at least one item to your pre-order";
    if (!formData.termsAccepted)
      newErrors.terms = "You must accept the terms and conditions";

    if (
      formData.paymentMethod === "saved_payment" &&
      !formData.savedPaymentMethodId
    ) {
      newErrors.payment = "Please select a saved payment method";
    }

    const mobileMethods = ["orange_money", "afrimoney", "wave", "mobile_money"];
    if (
      mobileMethods.includes(formData.paymentMethod) &&
      !formData.mobileMoneyNumber
    ) {
      newErrors.mobileMoney =
        "Mobile money number is required for this payment method";
    }
    return newErrors;
  }, [formData, cartItems]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});
    setErrorMessage(null);
    setSuccessMessage(null);

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSubmitting(false);
      const firstErrorField = Object.keys(newErrors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
      return;
    }

    try {
      const items = cartItems.map((item) => ({
        productId: selectedProduct?._id || null,
        productName: item.productName,
        productSlug: item.productSlug,
        category: item.category || "rumen-bypass-fat",
        packSizeKg: item.size,
        quantity: item.quantity,
      }));

      let paymentMethod = formData.paymentMethod;
      let savedPaymentMethodId = null;
      if (formData.paymentMethod === "saved_payment") {
        paymentMethod = "saved_payment";
        savedPaymentMethodId = formData.savedPaymentMethodId;
      }

      const preOrderData = {
        customerInfo: {
          fullName: formData.customerInfo.fullName.trim(),
          farmOrBusinessName: formData.customerInfo.farmOrBusinessName.trim(),
          phone: normalizePhone(formData.customerInfo.phone),
          whatsapp: normalizePhone(
            formData.customerInfo.whatsapp || formData.customerInfo.phone,
          ),
          email: formData.customerInfo.email.trim().toLowerCase(),
          location: formData.customerInfo.location.trim(),
          district: formData.customerInfo.district.trim(),
        },
        farmInfo: {
          customerType: formData.farmInfo.customerType,
          animalTypes:
            formData.farmInfo.animalTypes.length > 0
              ? formData.farmInfo.animalTypes
              : ["cattle"],
          animalCount: formData.farmInfo.animalCount || animalCount || 0,
          feedMixedAtOneTimeKg:
            formData.farmInfo.feedMixedAtOneTimeKg || feedPerDay || 250,
          notes: formData.farmInfo.notes.trim(),
        },
        items,
        paymentMethod,
        paymentReference: formData.paymentReference.trim(),
        mobileMoneyNumber: normalizePhone(formData.mobileMoneyNumber),
        savedPaymentMethodId,
        pickup: {
          type: formData.pickup.type,
          preferredDate: formData.pickup.preferredDate || null,
          address:
            formData.pickup.address.trim() ||
            "30 Kissy Road, Freetown, Sierra Leone",
          instructions: formData.pickup.instructions.trim(),
        },
        source: "website",
        termsAccepted: formData.termsAccepted,
        marketingConsent: formData.marketingConsent,
        customerNotes: formData.customerNotes.trim(),
        exchangeRate: { usdToSll: getRate() },
        currency: "SLL",
      };

      const response = await createPreOrder(preOrderData);
      const data = response.data;

      if (data.success) {
        const preOrder = data.data;
        setPreOrderNumber(preOrder.preOrderNumber || "");
        setSuccessMessage(
          `✅ Pre-order submitted successfully! Your pre-order number is ${preOrder.preOrderNumber || "processing"}.`,
        );
        setSubmitted(true);
        clearCart();
        setFormData((prev) => ({
          ...prev,
          termsAccepted: false,
          customerNotes: "",
          pickup: { ...prev.pickup, preferredDate: "", instructions: "" },
        }));
        setTimeout(() => {
          navigate(user ? "/dashboard?tab=preorders" : "/");
        }, 5000);
      } else {
        setErrorMessage(
          data.message || "Unable to submit pre-order. Please try again.",
        );
      }
    } catch (error) {
      console.error("Pre-order error:", error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to submit pre-order. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const getWhatsAppMessage = useCallback(() => {
    const itemsList = cartItems
      .map(
        (item) =>
          `${item.quantity}x ${item.size}kg ${item.productName} ($${item.preOrderPrice.toFixed(2)} each)`,
      )
      .join("\n");
    const rate = getRate();
    const paymentMethodLabel =
      PAYMENT_METHODS_CONFIG.find((m) => m.value === formData.paymentMethod)
        ?.label || formData.paymentMethod;

    let paymentDetails = `Payment Method: ${paymentMethodLabel}`;
    if (formData.paymentMethod === "saved_payment" && selectedSavedPayment) {
      paymentDetails += `\nSaved Payment: ${selectedSavedPayment.type === "card" ? `Card ending in ${selectedSavedPayment.last4}` : selectedSavedPayment.phoneNumber || selectedSavedPayment.type}`;
    }
    if (formData.mobileMoneyNumber) {
      paymentDetails += `\nMobile Money: ${formData.mobileMoneyNumber}`;
    }

    return `Hello Kenali Enterprise, I want to place a pre-order for Kenali GrowPro™.

📋 Customer Information:
Name: ${formData.customerInfo.fullName || "Not provided"}
Farm/Business: ${formData.customerInfo.farmOrBusinessName || "Not provided"}
Phone: ${formData.customerInfo.phone || "Not provided"}
Location: ${formData.customerInfo.location || "Not provided"}

📦 Order Details:
${itemsList}
Total Packs: ${totals.totalPacks}
Total Weight: ${totals.totalKg.toLocaleString()} kg

💰 Pricing (SLL - Primary):
Subtotal: ${formatSLL(totals.subtotalSLL)}
Deposit (50%): ${formatSLL(totals.depositSLL)}
Balance: ${formatSLL(totals.balanceSLL)}
You Save: ${formatSLL(totals.savingsSLL)} (${totals.savingsPercent.toFixed(1)}%)

💰 Pricing (USD - Reference):
Subtotal: $${totals.subtotalUSD.toFixed(2)}
Deposit (50%): $${totals.depositUSD.toFixed(2)}
Balance: $${totals.balanceUSD.toFixed(2)}
You Save: $${totals.savingsUSD.toFixed(2)}

🐄 Farm Info:
Animals: ${animalCount || formData.farmInfo.animalCount || 0}
Feed/Day: ${feedPerDay || formData.farmInfo.feedMixedAtOneTimeKg || 250}kg

${paymentDetails}

Please confirm my pre-order.`;
  }, [
    formData,
    cartItems,
    totals,
    animalCount,
    feedPerDay,
    getRate,
    selectedSavedPayment,
  ]);

  const handleWhatsAppOrder = useCallback(() => {
    const message = getWhatsAppMessage();
    window.open(
      `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`,
      "_blank",
    );
  }, [getWhatsAppMessage]);

  // ============================================
  // RENDER
  // ============================================
  const hasSavedMethods = hasSavedPaymentMethods
    ? hasSavedPaymentMethods()
    : paymentMethods.length > 0;

  return (
    <>
      <SEO
        title="Pre-Order Kenali GrowPro™ - Premium Livestock Feed"
        description="Pre-order Kenali GrowPro™ (Rumen Bypass Fat) with 50% deposit. Premium livestock nutrition supplement for cattle, goats, and sheep."
        keywords="pre-order livestock feed, Kenali GrowPro, rumen bypass fat, livestock nutrition, Sierra Leone farmers"
        canonical="https://kenalienterprise.com/preorder"
      />
      <motion.div
        className="preorder-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <div className="container">
          {/* Header */}
          <div className="preorder-header">
            <button className="back-btn" onClick={() => navigate(-1)}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back
            </button>
            <h1>
              <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order Kenali
              GrowPro™
            </h1>
            <p>
              Reserve your Kenali GrowPro™ (Rumen Bypass Fat) with a 50% deposit
            </p>
            <div className="badge-group">
              <span className="badge currency-badge">
                <FontAwesomeIcon icon={faMoneyBillWave} /> Primary: SLL (Le)
              </span>
              <span className="badge shipment-badge">
                <FontAwesomeIcon icon={faShip} /> First Shipment: 3,000 kg
              </span>
              <span className="badge mix-badge">
                <FontAwesomeIcon icon={faFlask} /> 1kg : 250kg Feed
              </span>
              <span className="badge preorder-badge">
                <FontAwesomeIcon icon={faPercent} /> 50% Deposit
              </span>
            </div>
          </div>

          {/* Messages */}
          <AnimatePresence>
            {successMessage && (
              <motion.div
                className="alert success"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <FontAwesomeIcon icon={faCheckCircle} />
                <div>
                  <strong>Success!</strong>
                  <span>{successMessage}</span>
                  {preOrderNumber && (
                    <span className="order-number">
                      Reference: {preOrderNumber}
                    </span>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {errorMessage && (
            <div className="alert error">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <div>
                <strong>Error!</strong>
                <span>{errorMessage}</span>
                <button
                  className="whatsapp-error-btn"
                  onClick={handleWhatsAppOrder}
                >
                  <FontAwesomeIcon icon={faWhatsappBrand} /> Contact Support
                </button>
              </div>
            </div>
          )}

          <div className="preorder-grid">
            {/* Left Column - Form */}
            <div className="preorder-form">
              <form onSubmit={handleSubmit} noValidate>
                {/* Customer Info */}
                <section className="form-section">
                  <h2>
                    <FontAwesomeIcon icon={faUser} /> Customer Information
                  </h2>
                  <p className="section-hint">
                    All fields marked with * are required
                  </p>

                  <div className="form-group">
                    <label htmlFor="fullName">Full Name *</label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.customerInfo.fullName}
                      onChange={(e) =>
                        handleInputChange(
                          "customerInfo",
                          "fullName",
                          e.target.value,
                        )
                      }
                      placeholder="Enter your full name"
                      className={errors.fullName ? "error" : ""}
                    />
                    {errors.fullName && (
                      <span className="error-text">{errors.fullName}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="farmOrBusinessName">
                      Farm or Business Name
                    </label>
                    <input
                      type="text"
                      id="farmOrBusinessName"
                      value={formData.customerInfo.farmOrBusinessName}
                      onChange={(e) =>
                        handleInputChange(
                          "customerInfo",
                          "farmOrBusinessName",
                          e.target.value,
                        )
                      }
                      placeholder="Enter farm or business name"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">
                        <FontAwesomeIcon icon={faPhone} /> Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.customerInfo.phone}
                        onChange={(e) =>
                          handleInputChange(
                            "customerInfo",
                            "phone",
                            e.target.value,
                          )
                        }
                        placeholder="+232 76 123 456"
                        className={errors.phone ? "error" : ""}
                      />
                      {errors.phone && (
                        <span className="error-text">{errors.phone}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="whatsapp">
                        <FontAwesomeIcon icon={faWhatsappBrand} /> WhatsApp
                      </label>
                      <input
                        type="tel"
                        id="whatsapp"
                        value={formData.customerInfo.whatsapp}
                        onChange={(e) =>
                          handleInputChange(
                            "customerInfo",
                            "whatsapp",
                            e.target.value,
                          )
                        }
                        placeholder="+232 76 123 456"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">
                      <FontAwesomeIcon icon={faEnvelope} /> Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.customerInfo.email}
                      onChange={(e) =>
                        handleInputChange(
                          "customerInfo",
                          "email",
                          e.target.value,
                        )
                      }
                      placeholder="your@email.com"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="location">
                        <FontAwesomeIcon icon={faMapMarkerAlt} /> Location *
                      </label>
                      <input
                        type="text"
                        id="location"
                        name="location"
                        value={formData.customerInfo.location}
                        onChange={(e) =>
                          handleInputChange(
                            "customerInfo",
                            "location",
                            e.target.value,
                          )
                        }
                        placeholder="City, District"
                        className={errors.location ? "error" : ""}
                      />
                      {errors.location && (
                        <span className="error-text">{errors.location}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="district">District</label>
                      <input
                        type="text"
                        id="district"
                        value={formData.customerInfo.district}
                        onChange={(e) =>
                          handleInputChange(
                            "customerInfo",
                            "district",
                            e.target.value,
                          )
                        }
                        placeholder="District"
                      />
                    </div>
                  </div>
                </section>

                {/* Farm Info */}
                <section className="form-section">
                  <h2>
                    <FontAwesomeIcon icon={faTractor} /> Farm Information
                  </h2>

                  <div className="form-group">
                    <label>Customer Type</label>
                    <div className="customer-type-grid">
                      {CUSTOMER_TYPES.map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          className={`customer-type-btn ${formData.farmInfo.customerType === type.value ? "active" : ""}`}
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              farmInfo: {
                                ...prev.farmInfo,
                                customerType: type.value,
                              },
                            }))
                          }
                        >
                          <FontAwesomeIcon icon={type.icon} />
                          <span>{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Animal Types</label>
                    <div className="animal-type-grid">
                      {ANIMAL_TYPES.map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          className={`animal-type-btn ${formData.farmInfo.animalTypes.includes(type.value) ? "active" : ""}`}
                          onClick={() => {
                            const current = formData.farmInfo.animalTypes;
                            const updated = current.includes(type.value)
                              ? current.filter((t) => t !== type.value)
                              : [...current, type.value];
                            setFormData((prev) => ({
                              ...prev,
                              farmInfo: {
                                ...prev.farmInfo,
                                animalTypes: updated,
                              },
                            }));
                          }}
                        >
                          <FontAwesomeIcon icon={faCow} />
                          <span>{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="farmAnimalCount">Animal Count</label>
                      <input
                        type="number"
                        id="farmAnimalCount"
                        value={formData.farmInfo.animalCount}
                        onChange={(e) =>
                          handleInputChange(
                            "farmInfo",
                            "animalCount",
                            parseInt(e.target.value) || 0,
                          )
                        }
                        min="0"
                        placeholder="Number of animals"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="feedMixedAtOneTimeKg">
                        Feed Mixed at One Time (kg)
                      </label>
                      <input
                        type="number"
                        id="feedMixedAtOneTimeKg"
                        value={formData.farmInfo.feedMixedAtOneTimeKg}
                        onChange={(e) =>
                          handleInputChange(
                            "farmInfo",
                            "feedMixedAtOneTimeKg",
                            parseInt(e.target.value) || 0,
                          )
                        }
                        min="0"
                        placeholder="Kg per batch"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="farmNotes">Additional Notes</label>
                    <textarea
                      id="farmNotes"
                      value={formData.farmInfo.notes}
                      onChange={(e) =>
                        handleInputChange("farmInfo", "notes", e.target.value)
                      }
                      placeholder="Any additional information about your farm"
                      rows="3"
                    />
                  </div>
                </section>

                {/* Pickup Info */}
                <section className="form-section">
                  <h2>
                    <FontAwesomeIcon icon={faMapMarkerAlt} /> Pickup Information
                  </h2>

                  <div className="form-group">
                    <label>Pickup Type</label>
                    <div className="pickup-type-grid">
                      {PICKUP_TYPES.map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          className={`pickup-type-btn ${formData.pickup.type === type.value ? "active" : ""}`}
                          onClick={() =>
                            handleInputChange("pickup", "type", type.value)
                          }
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="preferredDate">Preferred Date</label>
                    <input
                      type="date"
                      id="preferredDate"
                      value={formData.pickup.preferredDate}
                      onChange={(e) =>
                        handleInputChange(
                          "pickup",
                          "preferredDate",
                          e.target.value,
                        )
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="pickupAddress">Pickup Address</label>
                    <input
                      type="text"
                      id="pickupAddress"
                      value={formData.pickup.address}
                      onChange={(e) =>
                        handleInputChange("pickup", "address", e.target.value)
                      }
                      placeholder="Address"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="pickupInstructions">Instructions</label>
                    <textarea
                      id="pickupInstructions"
                      value={formData.pickup.instructions}
                      onChange={(e) =>
                        handleInputChange(
                          "pickup",
                          "instructions",
                          e.target.value,
                        )
                      }
                      placeholder="Special instructions for pickup or delivery"
                      rows="2"
                    />
                  </div>
                </section>

                {/* Payment Section */}
                <section className="form-section payment-section">
                  <h2>
                    <FontAwesomeIcon icon={faMoneyBillWave} /> Payment Method
                  </h2>

                  {user && hasSavedMethods && (
                    <div className="saved-payment-methods">
                      <label className="payment-option-label">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="saved_payment"
                          checked={formData.paymentMethod === "saved_payment"}
                          onChange={() =>
                            handlePaymentMethodChange("saved_payment")
                          }
                        />
                        <FontAwesomeIcon icon={faWallet} /> Use Saved Payment
                        Method
                      </label>
                      {formData.paymentMethod === "saved_payment" && (
                        <div className="saved-methods-list">
                          {paymentMethods.map((method) => (
                            <label
                              key={method._id}
                              className={`saved-method ${formData.savedPaymentMethodId === method._id ? "selected" : ""}`}
                            >
                              <input
                                type="radio"
                                name="savedPaymentMethodId"
                                value={method._id}
                                checked={
                                  formData.savedPaymentMethodId === method._id
                                }
                                onChange={() =>
                                  handleSavedPaymentSelect(method._id)
                                }
                              />
                              <FontAwesomeIcon
                                icon={
                                  method.type === "card"
                                    ? faCreditCard
                                    : faMobileAlt
                                }
                              />
                              <span>
                                {method.type === "card"
                                  ? `Card ending in ${method.last4}`
                                  : method.phoneNumber || method.type}
                              </span>
                              {method.isDefault && (
                                <span className="default-badge">Default</span>
                              )}
                              <button
                                type="button"
                                className="remove-payment-btn"
                                onClick={() =>
                                  handleRemoveSavedPayment(method._id)
                                }
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="payment-method-grid">
                    {PAYMENT_METHODS_CONFIG.map((method) => (
                      <button
                        key={method.value}
                        type="button"
                        className={`payment-method-btn ${formData.paymentMethod === method.value ? "active" : ""}`}
                        onClick={() => handlePaymentMethodChange(method.value)}
                      >
                        <FontAwesomeIcon icon={method.icon} />
                        {method.label}
                      </button>
                    ))}
                  </div>

                  {[
                    "orange_money",
                    "afrimoney",
                    "wave",
                    "mobile_money",
                  ].includes(formData.paymentMethod) && (
                    <div className="form-group mobile-money-field">
                      <label>
                        <FontAwesomeIcon icon={faMobileAlt} /> Mobile Money
                        Number *
                      </label>
                      <input
                        type="tel"
                        id="mobileMoneyNumber"
                        value={formData.mobileMoneyNumber}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            mobileMoneyNumber: e.target.value,
                          }))
                        }
                        placeholder="+232 76 123456"
                        className={errors.mobileMoney ? "error" : ""}
                      />
                      {errors.mobileMoney && (
                        <span className="error-text">{errors.mobileMoney}</span>
                      )}
                      <small>
                        Enter the phone number registered with your mobile money
                        account
                      </small>
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="paymentReference">Payment Reference</label>
                    <input
                      type="text"
                      id="paymentReference"
                      value={formData.paymentReference}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          paymentReference: e.target.value,
                        }))
                      }
                      placeholder="Optional reference"
                    />
                  </div>

                  {user && (
                    <button
                      type="button"
                      className="add-payment-btn"
                      onClick={() => setShowAddPayment(!showAddPayment)}
                    >
                      <FontAwesomeIcon icon={faPlus} />{" "}
                      {showAddPayment ? "Cancel" : "Add Payment Method"}
                    </button>
                  )}

                  {errors.payment && (
                    <span className="error-text">{errors.payment}</span>
                  )}

                  {showAddPayment && (
                    <div className="add-payment-form">
                      <h4>Add New Payment Method</h4>
                      <div className="form-group">
                        <label>Payment Type</label>
                        <select
                          name="type"
                          value={newPaymentMethod.type}
                          onChange={handleNewPaymentChange}
                        >
                          <option value="card">Card</option>
                          <option value="orange_money">Orange Money</option>
                          <option value="afrimoney">Afrimoney</option>
                          <option value="wave">WAVE</option>
                          <option value="mobile_money">Mobile Money</option>
                        </select>
                      </div>

                      {newPaymentMethod.type === "card" ? (
                        <>
                          <div className="form-group">
                            <label>Card Number</label>
                            <input
                              type="text"
                              name="cardNumber"
                              value={newPaymentMethod.cardNumber}
                              onChange={handleNewPaymentChange}
                              placeholder="1234 5678 9012 3456"
                            />
                          </div>
                          <div className="form-row">
                            <div className="form-group">
                              <label>Expiry Date</label>
                              <input
                                type="text"
                                name="expiryDate"
                                value={newPaymentMethod.expiryDate}
                                onChange={handleNewPaymentChange}
                                placeholder="MM/YY"
                              />
                            </div>
                            <div className="form-group">
                              <label>CVV</label>
                              <input
                                type="password"
                                name="cvv"
                                value={newPaymentMethod.cvv}
                                onChange={handleNewPaymentChange}
                                placeholder="123"
                                maxLength="4"
                              />
                            </div>
                          </div>
                          <div className="form-group">
                            <label>Name on Card</label>
                            <input
                              type="text"
                              name="nameOnCard"
                              value={newPaymentMethod.nameOnCard}
                              onChange={handleNewPaymentChange}
                              placeholder="John Doe"
                            />
                          </div>
                        </>
                      ) : (
                        <div className="form-group">
                          <label>Phone Number</label>
                          <input
                            type="tel"
                            name="phoneNumber"
                            value={newPaymentMethod.phoneNumber}
                            onChange={handleNewPaymentChange}
                            placeholder="+232 76 123456"
                          />
                        </div>
                      )}

                      <div className="form-group checkbox">
                        <label>
                          <input
                            type="checkbox"
                            name="isDefault"
                            checked={newPaymentMethod.isDefault}
                            onChange={handleNewPaymentChange}
                          />
                          Set as default payment method
                        </label>
                      </div>

                      <button
                        type="button"
                        className="save-payment-btn"
                        onClick={handleAddPaymentMethod}
                        disabled={paymentProcessing}
                      >
                        {paymentProcessing ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faCheck} /> Save Payment
                            Method
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </section>

                {/* Terms */}
                <section className="form-section terms-section">
                  <div className="form-group checkbox">
                    <label className={errors.terms ? "error-label" : ""}>
                      <input
                        type="checkbox"
                        checked={formData.termsAccepted}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            termsAccepted: e.target.checked,
                          }))
                        }
                      />
                      I agree to the pre-order terms: 50% deposit now, remaining
                      50% due before pickup or delivery.
                      {errors.terms && (
                        <span className="error-text">{errors.terms}</span>
                      )}
                    </label>
                  </div>

                  <div className="form-group checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.marketingConsent}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            marketingConsent: e.target.checked,
                          }))
                        }
                      />
                      I agree to receive marketing updates
                    </label>
                  </div>

                  <div className="form-group">
                    <label htmlFor="customerNotes">Additional Notes</label>
                    <textarea
                      id="customerNotes"
                      value={formData.customerNotes}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          customerNotes: e.target.value,
                        }))
                      }
                      placeholder="Any additional notes or special requests"
                      rows="2"
                    />
                  </div>
                </section>
              </form>
            </div>

            {/* Right Column - Cart */}
            <div className="preorder-cart">
              <div className="cart-header">
                <h2>
                  <FontAwesomeIcon icon={faShoppingCart} /> Your Pre-Order
                </h2>
                <span className="cart-count">{cartItems.length} items</span>
              </div>

              <div className="product-selector">
                <h3>
                  <FontAwesomeIcon icon={faAward} /> Kenali GrowPro™
                </h3>
                <p className="product-description">
                  Rumen Bypass Fat - Energy supplement for dairy cattle and
                  goats
                </p>

                <div className="size-selector">
                  {[25, 50].map((size) => {
                    const pricing = getPricingForSize(size);
                    const rate = getRate();
                    const preOrderSLL = pricing.preOrder * rate;
                    const depositSLL = pricing.deposit * rate;
                    const savingsSLL = pricing.savings * rate;

                    return (
                      <button
                        key={size}
                        type="button"
                        className={`size-btn ${selectedSize === size ? "active" : ""}`}
                        onClick={() => setSelectedSize(size)}
                      >
                        <span className="size-label">{size}kg</span>
                        <span className="size-price">
                          {formatSLL(preOrderSLL)}
                          <span className="size-price-usd">
                            {formatUSD(pricing.preOrder)}
                          </span>
                        </span>
                        <span className="size-deposit">
                          Deposit: {formatSLL(depositSLL)}
                        </span>
                        <span className="size-savings">
                          Save {formatSLL(savingsSLL)} ({pricing.savingsPercent}
                          %)
                        </span>
                        <span className="size-feed">
                          Mixes {pricing.feedMixKg.toLocaleString()}kg feed
                        </span>
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  className="add-product-btn"
                  onClick={addToCart}
                  disabled={submitted || !selectedProduct}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add to Cart
                </button>
              </div>

              <FeedCalculator
                selectedSize={selectedSize}
                animalCount={animalCount}
                setAnimalCount={setAnimalCount}
                feedPerDay={feedPerDay}
                setFeedPerDay={setFeedPerDay}
              />

              <div className="cart-items">
                {cartItems.length === 0 ? (
                  <div className="empty-cart">
                    <FontAwesomeIcon icon={faBox} />
                    <p>No items in your pre-order yet</p>
                    <span>Select a size and add to cart</span>
                  </div>
                ) : (
                  cartItems.map((item) => {
                    const totalPreOrder = item.preOrderPrice * item.quantity;
                    const totalRegular = item.regularPrice * item.quantity;
                    const totalSavings = totalRegular - totalPreOrder;
                    const itemSavingsPercent = item.savingsPercent || 7;
                    const rate = getRate();

                    return (
                      <div key={item.id} className="cart-item">
                        <div className="item-info">
                          <h4>{item.productName}</h4>
                          <span className="item-size">{item.size}kg pack</span>
                          <span className="item-mix">
                            Mixes{" "}
                            {(item.feedMixKg * item.quantity).toLocaleString()}{" "}
                            kg feed
                          </span>
                          <span className="item-price">
                            {formatSLL(totalPreOrder * rate)}
                            <span className="item-price-usd">
                              {formatUSD(totalPreOrder)}
                            </span>
                            <span className="item-savings">
                              Save {formatSLL(totalSavings * rate)} (
                              {itemSavingsPercent}%)
                            </span>
                          </span>
                        </div>
                        <div className="item-controls">
                          <div className="quantity-control">
                            <button
                              type="button"
                              onClick={() =>
                                updateQuantity(item.id, item.quantity - 1)
                              }
                              className="qty-btn"
                            >
                              <FontAwesomeIcon icon={faMinus} />
                            </button>
                            <span className="qty-display">{item.quantity}</span>
                            <button
                              type="button"
                              onClick={() =>
                                updateQuantity(item.id, item.quantity + 1)
                              }
                              className="qty-btn"
                            >
                              <FontAwesomeIcon icon={faPlus} />
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFromCart(item.id)}
                            className="remove-btn"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {cartItems.length > 0 && (
                <div className="cart-summary">
                  <div className="summary-row">
                    <span>Total Weight</span>
                    <span>{totals.totalKg.toLocaleString()} kg</span>
                  </div>
                  <div className="summary-row">
                    <span>Total Packs</span>
                    <span>{totals.totalPacks} packs</span>
                  </div>
                  <div className="summary-row">
                    <span>Regular Price</span>
                    <span>
                      {formatSLL(totals.regularTotalSLL)}
                      <span className="price-usd">
                        {formatUSD(totals.regularTotalUSD)}
                      </span>
                    </span>
                  </div>
                  <div className="summary-row savings">
                    <span>You Save</span>
                    <span>
                      {formatSLL(totals.savingsSLL)}
                      <span className="price-usd">
                        {formatUSD(totals.savingsUSD)}
                      </span>
                      ({totals.savingsPercent.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="summary-row deposit">
                    <span>Deposit (50%)</span>
                    <span>
                      {formatSLL(totals.depositSLL)}
                      <span className="price-usd">
                        {formatUSD(totals.depositUSD)}
                      </span>
                    </span>
                  </div>
                  <div className="summary-row balance">
                    <span>Balance Due</span>
                    <span>
                      {formatSLL(totals.balanceSLL)}
                      <span className="price-usd">
                        {formatUSD(totals.balanceUSD)}
                      </span>
                    </span>
                  </div>
                  <div className="summary-row total">
                    <span>Pre-Order Total</span>
                    <span>
                      {formatSLL(totals.subtotalSLL)}
                      <span className="price-usd">
                        {formatUSD(totals.subtotalUSD)}
                      </span>
                    </span>
                  </div>

                  {errors.cart && (
                    <div className="error-text cart-error">{errors.cart}</div>
                  )}

                  <button
                    type="button"
                    className="submit-btn"
                    onClick={handleSubmit}
                    disabled={submitting || cartItems.length === 0 || submitted}
                  >
                    {submitting ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin /> Submitting...
                      </>
                    ) : submitted ? (
                      <>
                        <FontAwesomeIcon icon={faCheckCircle} /> Submitted
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faClipboardCheck} /> Submit
                        Pre-Order
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    className="whatsapp-order-btn"
                    onClick={handleWhatsAppOrder}
                  >
                    <FontAwesomeIcon icon={faWhatsappBrand} /> Order via
                    WhatsApp
                  </button>

                  <p className="disclaimer">
                    <FontAwesomeIcon icon={faInfoCircle} /> A Kenali Enterprise
                    representative will contact you within 24 hours to confirm
                    your deposit and order details.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default PreOrder;
